import cn from 'classnames';
import {
  FC,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMatch } from 'react-router';

import { PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Button, toastCaller } from '@/shared/ui';

import { organizationModel } from '@/entities/organization';
import { ChecklistPanel } from '@/entities/templates';
import {
  trackRecordingEvent,
  transcriptorMessages,
  transcriptorModel,
  useNetworkStatus,
  useRecordingBeforeUnload,
  useRecordingCompletion,
  useRecordingInterruptionDetection,
} from '@/entities/transcriptor';
import { userModel } from '@/entities/user';

import {
  audioRecordingService,
  liveSession,
  useLiveSession,
  useLiveSessionFollower,
  useRecordingSession,
} from '@/features/audioRecording';

import { DragState, RecordingToastPosition } from '../../config';
import {
  useDictationCountdown,
  useOpenRecordingPage,
  useRecordingChecklists,
  useRecordingStop,
} from '../../hooks';
import {
  clampRecordingToastPosition,
  getInitialRecordingToastPosition,
  getToastSize,
  getViewportSize,
  saveRecordingToastPosition,
} from '../../lib';
import {
  RecordingControls,
  RecordingStatus,
} from '../RecordingControls/RecordingControls';

import { ClassicRecordingToast } from './ClassicRecordingToast';
import styles from './RecordingToast.module.scss';

const { resetRecording } = transcriptorModel.actions;

const {
  selectConsultationId,
  selectFragmentId,
  selectIsUploading,
  selectIsProcessing,
  selectIsRecording,
  selectIsOffline,
  selectIsInterrupted,
  selectPatientId,
  selectRecordingEncounterKind,
  selectRecordingSchemaId,
  selectUploadProgress,
} = transcriptorModel.selectors;

// The recording's corner window, mounted once for the whole app: it follows
// the recording on every page and gives way to the recording's own page, which
// shows the same controls. Expand opens that page.
const V2RecordingToast: FC = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const recordingsPatientId = useMatch(PATHS.patientRecordings)?.params
    .patientID;
  const recordingPage = useMatch(PATHS.patientRecording)?.params;
  const currentUserID = useAppSelector(userModel.selectors.selectCurrentUserID);
  const isRecording = useAppSelector(selectIsRecording);
  const isUploading = useAppSelector(selectIsUploading);
  const isProcessing = useAppSelector(selectIsProcessing);
  const uploadProgress = useAppSelector(selectUploadProgress);
  const consultationId = useAppSelector(selectConsultationId);
  const fragmentId = useAppSelector(selectFragmentId);
  const patientId = useAppSelector(selectPatientId);
  const isOffline = useAppSelector(selectIsOffline);
  const isInterrupted = useAppSelector(selectIsInterrupted);
  const isTreatment =
    useAppSelector(selectRecordingEncounterKind) === 'treatment';
  const [position, setPosition] = useState<RecordingToastPosition | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isChecklistOpen, setIsChecklistOpen] = useState(false);
  const toastRef = useRef<HTMLDivElement>(null);
  const positionRef = useRef<RecordingToastPosition | null>(null);
  const dragStateRef = useRef<DragState | null>(null);

  const organizationId = useAppSelector(
    transcriptorModel.selectors.selectRecordingOrganizationId,
  );
  const allowed = useAppSelector(
    organizationModel.selectors.selectOrganizationTranscriptorV2Enabled(
      organizationId,
    ),
  );
  useEffect(() => {
    if (allowed === false) {
      audioRecordingService.kill();
      liveSession.clear();
      dispatch(resetRecording());
    }
  }, [allowed, dispatch]);

  const isVisible = isRecording || isUploading || isProcessing || isInterrupted;
  const session = useLiveSession();
  const ownSession =
    session && session.meta.encounterId === consultationId ? session : null;
  const shownOnPage = !!useRecordingSession(recordingPage?.recordId);

  useLiveSessionFollower();

  const remainingSeconds = useDictationCountdown();
  const stop = useRecordingStop();
  const openRecordingPage = useOpenRecordingPage();

  const schemaId = useAppSelector(selectRecordingSchemaId);
  const checklistTabs = useRecordingChecklists(
    isVisible ? schemaId : undefined,
    organizationId,
  );

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  useNetworkStatus();
  useRecordingBeforeUnload();

  useEffect(() => {
    audioRecordingService.setOnlineStatus(!isOffline);
  }, [isOffline]);

  const getLastActivityTimestamp = useCallback(
    () => audioRecordingService.getLastDataTimestamp(),
    [],
  );
  const hasCaptureEnded = useCallback(
    () => audioRecordingService.hasTrackEnded(),
    [],
  );
  // Capture is released only for an ended track: otherwise the browser keeps
  // showing a live mic while the toast says the recording stopped.
  const handleInterrupted = useCallback(
    () => audioRecordingService.suspendForInterruption(),
    [],
  );
  const handleSignalLostChange = useCallback(
    (isLost: boolean) =>
      trackRecordingEvent(isLost ? 'signal_lost' : 'signal_restored', {
        encounter_id: consultationId,
        fragment_id: fragmentId,
      }),
    [consultationId, fragmentId],
  );
  useRecordingInterruptionDetection({
    getLastActivityTimestamp,
    hasCaptureEnded,
    onInterrupted: handleInterrupted,
    onSignalLostChange: handleSignalLostChange,
  });

  useEffect(() => {
    if (isRecording && !isProcessing && !audioRecordingService.isRecording()) {
      // No consultation data means a real failure, not just a post-navigation reinit
      if (!consultationId || !fragmentId) {
        dispatch(resetRecording());
      }
    }
  }, [isRecording, isProcessing, consultationId, fragmentId]);

  // Stop opened the page of a recording started from its start page, or of
  // one stopped on the patient's recordings; the clinician may have gone on
  // since, and stays there.
  const handleConsultationComplete = (completedConsultationId: string) => {
    dispatch(resetRecording());

    if (!patientId) {
      return;
    }

    void dispatch(
      transcriptorModel.thunks.fetchConsultationById({
        consultationId: completedConsultationId,
        patientId,
      }),
    );

    if (
      ownSession ||
      recordingsPatientId === patientId ||
      recordingPage?.patientID === patientId
    ) {
      return;
    }

    openRecordingPage(patientId, completedConsultationId);
  };

  useRecordingCompletion({
    consultationId,
    patientId,
    isProcessing,
    onComplete: handleConsultationComplete,
  });

  // A dictation stops at its limit as Finish stops it: on its countdown, or
  // when scribe refuses its live part past the limit.
  const dictationLimitReached =
    remainingSeconds === 0 || ownSession?.live?.reason === 'dictationLimit';
  useEffect(() => {
    if (dictationLimitReached && isRecording) {
      toastCaller({
        type: 'warning',
        message: intl.formatMessage(transcriptorMessages.dictationLimitReached),
        autoClose: 10000,
      });
      void stop();
    }
    // Stops once, when the limit is reached.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictationLimitReached]);

  useLayoutEffect(() => {
    if (!isVisible) {
      setPosition(null);
      dragStateRef.current = null;
      setIsDragging(false);
      setIsChecklistOpen(false);
      return;
    }

    setPosition(
      getInitialRecordingToastPosition({
        currentUserID,
        toastElement: toastRef.current,
      }),
    );
  }, [currentUserID, isVisible, shownOnPage]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const handleResize = () => {
      setPosition((currentPosition) => {
        if (!currentPosition) {
          return getInitialRecordingToastPosition({
            currentUserID,
            toastElement: toastRef.current,
          });
        }

        return clampRecordingToastPosition({
          position: currentPosition,
          size: getToastSize(toastRef.current),
          viewport: getViewportSize(),
        });
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [currentUserID, isVisible]);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;

      if (!dragState) {
        return;
      }

      if (!Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) {
        return;
      }

      const nextPosition = clampRecordingToastPosition({
        position: {
          x: dragState.startPosition.x + event.clientX - dragState.pointerX,
          y: dragState.startPosition.y + event.clientY - dragState.pointerY,
        },
        size: getToastSize(toastRef.current),
        viewport: getViewportSize(),
      });

      positionRef.current = nextPosition;
      setPosition(nextPosition);
    };

    const handlePointerUp = () => {
      dragStateRef.current = null;
      setIsDragging(false);

      if (positionRef.current && currentUserID) {
        saveRecordingToastPosition({
          position: positionRef.current,
          storage: window.localStorage,
          userID: currentUserID,
        });
      }
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [currentUserID, isDragging]);

  const handleDragStart = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) {
        return;
      }

      if (!positionRef.current) {
        const initialPosition = getInitialRecordingToastPosition({
          currentUserID,
          toastElement: toastRef.current,
        });
        positionRef.current = initialPosition;
        setPosition(initialPosition);
      }

      if (!positionRef.current) {
        return;
      }

      event.preventDefault();

      setIsChecklistOpen(false);

      dragStateRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        startPosition: positionRef.current,
      };
      setIsDragging(true);
    },
    [currentUserID],
  );

  if (!isVisible || shownOnPage || allowed === false) {
    return null;
  }

  const title =
    ownSession?.meta.title ||
    (isTreatment ? (
      <FormattedMessage
        id="recordingToast.recordingTreatment"
        defaultMessage="Recording treatment"
      />
    ) : (
      <FormattedMessage
        id="recordingToast.recording"
        defaultMessage="Recording consultation"
      />
    ));

  return (
    <div
      ref={toastRef}
      data-component="recording-toast"
      className={cn(styles.recordingToast, {
        [styles.dragging]: isDragging,
      })}
      style={
        position
          ? {
              bottom: 'auto',
              left: `${position.x}px`,
              top: `${position.y}px`,
            }
          : undefined
      }
    >
      {isChecklistOpen && checklistTabs.length > 0 && (
        <ChecklistPanel
          className={styles.checklistPanel}
          tabs={checklistTabs}
          triggerRef={toastRef}
          onClose={() => setIsChecklistOpen(false)}
        />
      )}

      <div className={styles.header}>
        <div className={styles.heading}>
          <h3
            className={cn('h5', styles.title)}
            title={ownSession?.meta.title || undefined}
          >
            {title}
          </h3>
          <RecordingStatus />
        </div>
        {ownSession && patientId && consultationId && (
          <Button
            variant="tertiary"
            size="small"
            icon="arrowUp"
            className={styles.expandButton}
            aria-label={intl.formatMessage({
              id: 'recordingToast.expand',
              defaultMessage: 'Expand',
            })}
            onClick={() => {
              setIsChecklistOpen(false);
              openRecordingPage(patientId, consultationId);
            }}
          />
        )}
        {checklistTabs.length > 0 &&
          !ownSession?.live &&
          !isProcessing &&
          !isInterrupted && (
            <Button
              variant="gray"
              size="small"
              className={styles.checklistButton}
              onClick={() => setIsChecklistOpen((isOpen) => !isOpen)}
            >
              <FormattedMessage
                id="recordingToast.openChecklist"
                defaultMessage="Open checklist"
              />
            </Button>
          )}
        <button
          type="button"
          className={styles.dragHandleButton}
          aria-label={intl.formatMessage({
            id: 'recordingToast.moveWidget',
            defaultMessage: 'Move recording widget',
          })}
          onPointerDown={handleDragStart}
        >
          <span className={styles.dragHandle} aria-hidden />
        </button>
      </div>

      <RecordingControls />

      {isProcessing && (
        <div className={styles.progressSection}>
          <p className={styles.progressTitle}>
            {uploadProgress > 0 ? (
              <FormattedMessage
                id="recordingToast.saving"
                defaultMessage="Saving consultation..."
              />
            ) : (
              <FormattedMessage
                id="recordingToast.processing"
                defaultMessage="Processing recording..."
              />
            )}
          </p>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={
                {
                  '--progress-width': `${uploadProgress}%`,
                } as React.CSSProperties
              }
            />
          </div>
          <p className={styles.progressText}>
            <FormattedMessage
              id="recordingToast.uploadProgress"
              defaultMessage="{progress}% complete"
              values={{ progress: uploadProgress }}
            />
          </p>
        </div>
      )}
    </div>
  );
};

export const RecordingToast: FC = () => {
  const v2 = useAppSelector(
    transcriptorModel.selectors.selectRecordingTranscriptorV2,
  );
  return v2 ? <V2RecordingToast /> : <ClassicRecordingToast />;
};
